/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import axios from "axios";
import dayjs from "dayjs";

import { IInspectionRecord } from "../../model/inspection-record.model";
import { IInspectionRecordGroup } from "../../model/inspection-record-group.model";
import { IInspectionRecordGroupEquipment } from "../../model/inspection-record-group-equipment.model";
import { IPlant } from "../../model/plant.model";
import { TreeNodeRoute } from "pages/thermography/components/InspectionRouteTree/types/treeNode";
// import { backendRecordGroupsToNodes } from "../mappers/inspectionRecordTreeMapper";

import { inspectionRecordAdapter } from "../mappers/adapters/inspectionRecordAdapter";
import { backendGroupsToTreeNodes } from "../../components/InspectionRouteTree/mappers/genericTreeMapper";
import { EquipmentInspectionStatus } from "../utils/enumEquipmentInspectionStatus";
import { applyNodeStatusesToRecordGroups } from "../../InspectionRoute/utils/functionsTreeNode";
import { findEquipmentInGroups, findGroupInGroups, updateEquipmentStatusInGroups, updateEquipmentStatusInNodes, updateGroupFinishInGroups } from "pages/thermography/InspectionRoute/utils/functionsTreeNode";
import { DatabaseService } from "pages/thermography/stores/DatabaseService";

export interface InspectionRecordState {
    data: IInspectionRecord | null;
    nodes: TreeNodeRoute[];
    groups: IInspectionRecordGroup[];
    allRecords: IInspectionRecord[];
    loading: boolean;
    error: string | null;
}

const emptyRecord: IInspectionRecord = {
    id: undefined,
    code: "",
    name: "",
    description: "",
    maintenanceDocument: "",
    createdAt: dayjs().toISOString(),
    expectedStartDate: undefined,
    expectedEndDate: undefined,
    started: false,
    startedAt: null,
    finished: false,
    finishedAt: null,
    plant: undefined,
    inspectionRoute: null,
    createdBy: undefined,
    startedBy: null,
    finishedBy: null,
    groups: []
};

const initialState: InspectionRecordState = {
    data: emptyRecord,
    nodes: [],
    groups: [],
    allRecords: [],
    loading: false,
    error: null,
};

function injectAnomaliesIntoGroups(
    recordId: string | undefined,
    groups: any[],
    plantId?: string,
    anomalyRecords: any[] = [],
    components: any[] = []
): any[] {
    const componentNameById = new Map<string, string>();

    for (const c of components) {
        const id = c?.id ?? c?.component?.id;
        if (id == null) continue;

        componentNameById.set(String(id), String(c?.name));
    }

    const getComponentName = (equipmentComponentId: any) => {
        if (equipmentComponentId == null) return null;
        return componentNameById.get(String(equipmentComponentId)) ?? null;
    };

    const visit = (list?: any[]) => {
        if (!Array.isArray(list)) return;

        list.forEach(group => {
            if (Array.isArray(group.equipments)) {
                group.equipments = group.equipments.map((eq: any) => {
                    const enriched = anomalyRecords
                        .filter(
                            (item: any) =>
                                item.plant?.id === plantId &&
                                item.equipment?.id === eq.equipment?.id &&
                                item.route?.id === recordId
                        )
                        .map((item: any) => ({
                            ...item,
                            equipmentComponentName: getComponentName(item.component?.id),
                        }));

                    return {
                        ...eq,
                        anomalyRecords: enriched,
                    };
                });
            }

            if (Array.isArray(group.subGroups)) visit(group.subGroups);
        });
    };

    const cloned = structuredClone(groups);
    visit(cloned);
    return cloned;
}


export const getAllRecords = createAsyncThunk(
    "inspectionRecord/getAllRecords",
    async (_, thunkAPI) => {
        try {

            const res = await axios.get("/api/inspection-records");

            return res.data as IInspectionRecord[];
        } catch (err) {
            return thunkAPI.rejectWithValue("Erro ao carregar registros");
        }
    }
);

export const getRecordById = createAsyncThunk(
    "inspectionRecord/getRecordById",
    async (id: string, thunkAPI) => {
        try {
            const db = DatabaseService.getInstance();

            const res = await axios.get(`/api/inspection-records/actions/${id}`);
            const record = res.data as IInspectionRecord;

            const plantId = record.plant?.id;
            const anomalies = db.getAnomalyRecords();
            const components = db.getEquipmentComponents();

            const groupsWithAnomalies = injectAnomaliesIntoGroups(
                record.id, 
                record.groups ?? [],
                plantId,
                anomalies,
                components
            );

            return {
                ...record,
                groups: groupsWithAnomalies,
            } as IInspectionRecord;
        } catch (err) {
            return thunkAPI.rejectWithValue("Erro ao carregar registro");
        }
    }
);


export const updateRecord = createAsyncThunk(
    "inspectionRecord/updateRecord",
    async (id: string, thunkAPI) => {
        const state = thunkAPI.getState() as any;
        const { data } = state.inspectionRecord as InspectionRecordState;

        if (!data) {
            return thunkAPI.rejectWithValue("Registro não carregado");
        }

        try {
            const { groups: _groups, ...payload } = data;
            const res = await axios.put(
                `/api/inspection-records/${id}`,
                payload
            );

            return res.data as IInspectionRecord;
        } catch (err) {
            return thunkAPI.rejectWithValue("Erro ao atualizar registro");
        }
    }
);

export const updateRecordWithGroups = createAsyncThunk(
    "inspectionRecord/updateRecordWithGroups",
    async (id: string, thunkAPI) => {
        const state = thunkAPI.getState() as any;
        const { data, nodes, groups } = state.inspectionRecord as InspectionRecordState;

        if (!data) {
            return thunkAPI.rejectWithValue("Registro não carregado");
        }

        try {
            const groupsWithStatuses = applyNodeStatusesToRecordGroups(nodes, groups);
            const payload = { ...data, groups: groupsWithStatuses };

            const res = await axios.put(
                `/api/inspection-records/actions/update-record/${id}`,
                payload
            );

            return res.data as IInspectionRecord;
        } catch (err) {
            return thunkAPI.rejectWithValue("Erro ao concluir inspeção");
        }
    }
);

export const updateRecordGroupEquipment = createAsyncThunk(
    "inspectionRecord/updateRecordGroupEquipment",
    async (
        { id, status }: { id: string; status: EquipmentInspectionStatus },
        thunkAPI
    ) => {
        const state = thunkAPI.getState() as any;
        const { groups } = state.inspectionRecord as InspectionRecordState;

        const equipment = findEquipmentInGroups(groups, id);

        if (!equipment) {
            return thunkAPI.rejectWithValue("Equipamento não encontrado");
        }

        try {
            const payload = { ...equipment, status };

            const res = await axios.put(
                `/api/inspection-record-group-equipments/${id}`,
                payload
            );

            return res.data as IInspectionRecordGroupEquipment;
        } catch (err) {
            return thunkAPI.rejectWithValue("Erro ao atualizar equipamento");
        }
    }
);

export const updateRecordGroup = createAsyncThunk(
    "inspectionRecord/updateRecordGroup",
    async (id: string, thunkAPI) => {
        const state = thunkAPI.getState() as any;
        const { groups } = state.inspectionRecord as InspectionRecordState;

        const group = findGroupInGroups(groups, id);

        if (!group) {
            return thunkAPI.rejectWithValue("Grupo não encontrado");
        }

        try {
            const finishedAt = dayjs().toISOString();
            const payload = {
                ...group,
                finished: true,
                finishedAt
            } as any;

            const res = await axios.put(
                `/api/inspection-record-groups/actions/update-record-group/${id}`,
                payload
            );

            return res.data as IInspectionRecordGroup;
        } catch (err) {
            return thunkAPI.rejectWithValue("Erro ao atualizar grupo");
        }
    }
);


const inspectionRecordSlice = createSlice({
    name: "inspectionRecord",
    initialState,
    reducers: {
        resetRecord(state) {
            state.data = { ...emptyRecord, createdAt: dayjs().toISOString() };
            state.groups = [];
            state.error = null;
        },

        setName(state, action: PayloadAction<string>) {
            if (state.data) state.data.name = action.payload;
        },
        setDescription(state, action: PayloadAction<string>) {
            if (state.data) state.data.description = action.payload;
        },
        setPlant(state, action: PayloadAction<IPlant | undefined>) {
            if (state.data) state.data.plant = action.payload;
        },
        setMaintenanceDocument(state, action: PayloadAction<string | null>) {
            if (state.data) state.data.maintenanceDocument = action.payload;
        },

        setGroups(state, action: PayloadAction<IInspectionRecordGroup[]>) {
            state.groups = action.payload;
        },
        setNodes(state, action: PayloadAction<TreeNodeRoute[]>) {
            state.nodes = action.payload;
        },
        setStarted(state) {
            if (!state.data) return;
            state.data.started = true;
            state.data.startedAt = dayjs().toISOString();
        },
        setFinished(state) {
            if (!state.data) return;
            state.data.finished = true;
            state.data.finishedAt = dayjs().toISOString();
        },
    },

    extraReducers: (builder) => {
        builder
            .addCase(getAllRecords.pending, (state) => {
                state.loading = true;
            })
            .addCase(getAllRecords.fulfilled, (state, action) => {
                state.loading = false;
                state.allRecords = action.payload;
                state.error = null;
            })
            .addCase(getAllRecords.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            });

        builder
            .addCase(getRecordById.pending, (state) => {
                state.loading = true;
                state.nodes = [];
            })
            .addCase(getRecordById.fulfilled, (state, action) => {
                state.loading = false;

                const record = action.payload;

                state.data = record;
                state.groups = record.groups ?? [];

                state.nodes = backendGroupsToTreeNodes(
                    state.groups,
                    inspectionRecordAdapter
                );

                state.error = null;
            })
            .addCase(getRecordById.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            })
            .addCase(updateRecord.fulfilled, (state, action) => {
                state.data = { ...state.data, ...action.payload };
                state.error = null;
            })
            .addCase(updateRecord.rejected, (state, action) => {
                state.error = action.payload as string;
            })
            .addCase(updateRecordWithGroups.fulfilled, (state, action) => {
                state.data = { ...state.data, ...action.payload };
                state.groups = action.payload.groups ?? state.groups;
                state.error = null;
            })
            .addCase(updateRecordWithGroups.rejected, (state, action) => {
                state.error = action.payload as string;
            })
            .addCase(updateRecordGroupEquipment.fulfilled, (state, action) => {
                const updatedEquipment = action.payload;
                if (!updatedEquipment?.id) return;

                const status = (updatedEquipment.status as EquipmentInspectionStatus) ?? null;

                updateEquipmentStatusInGroups(state.groups, updatedEquipment.id, status);
                updateEquipmentStatusInNodes(state.nodes, updatedEquipment.id, status);

                state.error = null;
            })
            .addCase(updateRecordGroupEquipment.rejected, (state, action) => {
                state.error = action.payload as string;
            })
            .addCase(updateRecordGroup.fulfilled, (state, action) => {
                const updatedGroup = action.payload;
                if (!updatedGroup?.id) return;

                updateGroupFinishInGroups(
                    state.groups,
                    updatedGroup.id,
                    updatedGroup.finished ?? true,
                    updatedGroup.finishedAt ? String(updatedGroup.finishedAt) : null
                );

                state.error = null;
            })
            .addCase(updateRecordGroup.rejected, (state, action) => {
                state.error = action.payload as string;
            });
    },
});

export const {
    resetRecord,
    setName,
    setDescription,
    setPlant,
    setMaintenanceDocument,
    setGroups,
    setNodes,
    setStarted,
    setFinished
} = inspectionRecordSlice.actions;

export default inspectionRecordSlice.reducer;
